class EquiBowlCartQueue {
  constructor(api, cart, onChange = () => {}) {
    this.api = api;
    this.cart = cart;
    this.onChange = onChange;
    this.intents = new Map();
    this.noteIntent = undefined;
    this.busy = false;
    this.error = null;
    this.needsRead = true;
    this.revision = 0;
    this.savedRevision = 0;
    this.work = Promise.resolve();
  }
  static target(item) {
    return { id: String(item.variant_id || item.id), properties: item.properties || {}, sellingPlan: item.selling_plan_allocation?.selling_plan?.id || null };
  }
  static identity(target) {
    return JSON.stringify([String(target.id), target.sellingPlan ? String(target.sellingPlan) : null, Object.entries(target.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);
  }
  items(target) {
    const id = EquiBowlCartQueue.identity(target);
    return this.cart.items.filter(item => EquiBowlCartQueue.identity(EquiBowlCartQueue.target(item)) === id);
  }
  quantity(target) { return this.items(target).reduce((sum, item) => sum + item.quantity, 0); }
  desired(target) { return this.intents.get(EquiBowlCartQueue.identity(target))?.quantity ?? this.quantity(target); }
  get pending() { return this.busy || this.needsRead || this.intents.size > 0 || this.noteIntent !== undefined; }
  emit() { this.onChange(this); }
  set(target, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 999) return;
    this.intents.set(EquiBowlCartQueue.identity(target), { target, quantity, revision: ++this.revision });
    this.emit();
    this.run();
  }
  adjust(target, delta) { this.set(target, Math.max(0, this.desired(target) + delta)); }
  setNote(note, start = true) {
    this.noteIntent = { note, revision: ++this.revision };
    this.emit();
    if (start) this.run();
  }
  async refresh() { this.needsRead = true; return this.run(); }
  async retry() { this.error = null; this.needsRead = true; return this.run(); }
  async discard() {
    if (this.busy) return;
    this.intents.clear(); this.noteIntent = undefined; this.error = null; this.revision++; this.needsRead = true;
    return this.run();
  }
  run() {
    if (this.busy || this.error) return this.work;
    this.busy = true;
    this.emit();
    this.work = (async () => {
      try {
        if (this.needsRead) { this.cart = await this.api.read(); this.needsRead = false; this.emit(); }
        while (this.intents.size || this.noteIntent !== undefined) {
          const next = this.intents.entries().next().value;
          let expected = null;
          let intent = null;
          let identity = null;
          let note = null;
          try {
            if (next) {
              [identity, intent] = next;
              const items = this.items(intent.target);
              const current = this.quantity(intent.target);
              if (current === intent.quantity) {
                if (this.intents.get(identity) === intent) this.intents.delete(identity);
                continue;
              }
              if (current > intent.quantity) {
                const item = items[items.length - 1];
                const reduction = Math.min(item.quantity, current - intent.quantity);
                expected = current - reduction;
                this.cart = await this.api.change(item.key, item.quantity - reduction);
              } else if (items.length) {
                expected = intent.quantity;
                this.cart = await this.api.change(items[0].key, items[0].quantity + intent.quantity - current);
              } else {
                expected = intent.quantity;
                await this.api.add(intent.target, intent.quantity);
                this.cart = await this.api.read();
              }
              if (this.quantity(intent.target) !== expected) throw new Error(this.api.quantityError);
              if (this.intents.get(identity) === intent && this.quantity(intent.target) === intent.quantity) this.intents.delete(identity);
            } else {
              note = this.noteIntent;
              this.cart = await this.api.note(note.note);
              if ((this.cart.note || '') !== note.note) throw new Error(this.api.error);
              if (this.noteIntent === note) this.noteIntent = undefined;
            }
            this.savedRevision++;
            this.emit();
          } catch (error) {
            let recovered = false;
            try {
              this.cart = await this.api.read();
              this.needsRead = false;
              if (intent && expected !== null && this.quantity(intent.target) === expected) {
                if (this.intents.get(identity) === intent && expected === intent.quantity) this.intents.delete(identity);
                recovered = true;
              } else if (note && (this.cart.note || '') === note.note) {
                if (this.noteIntent === note) this.noteIntent = undefined;
                recovered = true;
              }
            } catch (_) { this.needsRead = true; }
            if (!recovered) throw error;
            this.savedRevision++;
            this.emit();
          }
        }
      } catch (error) {
        this.error = error instanceof Error ? error : new Error(this.api.error);
      } finally {
        this.busy = false;
        this.emit();
      }
    })();
    return this.work;
  }
}
if (typeof module !== 'undefined' && module.exports) module.exports = EquiBowlCartQueue;
if (typeof document !== 'undefined') (() => {
  'use strict';
  if (window.equibowlCartReady) return;
  const configNode = document.getElementById('eq-cart-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  window.equibowlCartReady = true;
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: config.currency || 'INR', minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);
  let noteTimer;
  let feedbackTimer;
  let sectionTimer;
  let sectionSerial = 0;
  let renderedRevision = 0;
  const targets = new Map();
  const formTarget = form => {
    const data = new FormData(form);
    const properties = {};
    for (const [name, value] of data.entries()) {
      const match = /^properties\[([^\]]+)\]$/.exec(name);
      if (match && typeof value === 'string' && value.trim()) properties[match[1]] = value.trim();
    }
    return { id: String(data.get('id')), properties, sellingPlan: data.get('selling_plan') || null };
  };
  const tomorrowIST = () => new Date(Date.now() + 19800000 + 86400000).toISOString().slice(0, 10);
  const setStartMinimum = form => {
    const input = form.querySelector('[data-eq-start-date]');
    if (input) input.min = tomorrowIST();
  };
  const invalidPlanDates = () => Array.from(document.querySelectorAll('[data-eq-plan-start]')).some(row => row.dataset.eqPlanStart < tomorrowIST() || !/^\d{4}-\d{2}-\d{2}$/.test(row.dataset.eqPlanStart));
  const request = async (path, body) => {
    const response = await fetch(`${root}${path}`, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
    let result;
    try { result = await response.json(); } catch (_) { throw new Error(config.sync_error); }
    if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : config.sync_error);
    return result;
  };
  const api = {
    read: () => request('cart.js'),
    change: (id, quantity) => request('cart/change.js', { id, quantity }),
    add: (target, quantity) => request('cart/add.js', { items: [{ id: target.id, quantity, properties: target.properties, ...(target.sellingPlan ? { selling_plan: target.sellingPlan } : {}) }] }),
    note: note => request('cart/update.js', { note }),
    error: config.sync_error,
    quantityError: config.sync_error
  };
  const getRowTarget = row => {
    if (row.dataset.eqIdentity && targets.has(row.dataset.eqIdentity)) return targets.get(row.dataset.eqIdentity);
    const item = queue.cart.items.find(item => item.key === row.dataset.key);
    if (!item) return null;
    const target = EquiBowlCartQueue.target(item);
    const identity = EquiBowlCartQueue.identity(target);
    row.dataset.eqIdentity = identity;
    targets.set(identity, target);
    return target;
  };
  const updateProductVariant = select => {
    const form = select.closest('form');
    const option = select.selectedOptions[0];
    form.querySelector('button[type="submit"]').disabled = option.dataset.available !== 'true';
    const price = document.querySelector('[data-eq-product-price]');
    if (price) price.textContent = option.dataset.price;

    document.querySelectorAll('[data-eq-nutrition-variant]').forEach(node => { node.hidden = node.dataset.eqNutritionVariant !== select.value; });
  };
  const setupPlanForm = (form, fromOptions = false) => {
    const source = form.querySelector('[data-eq-prepaid-variants]');
    const master = form.querySelector('[data-eq-variant-select]');
    if (!source || !master) return;
    const variants = JSON.parse(source.textContent);
    const controls = Array.from(form.querySelectorAll('[data-eq-plan-option]'));
    const selected = fromOptions ? variants.find(variant => controls.every((control, index) => variant.options[index] === control.value)) : variants.find(variant => String(variant.id) === master.value);
    if (!selected) { form.querySelector('button[type="submit"]').disabled = true; return; }
    master.value = String(selected.id);
    controls.forEach((control, index) => { control.value = selected.options[index]; });
    form.querySelector('[data-eq-plan-options]').hidden = false;
    form.querySelector('[data-eq-plan-fallback]').hidden = true;
    updateProductVariant(master);
    const days = Number(selected.options[2].match(/(\d+) days/)?.[1] || 0);
    const meals = days * (selected.options[1] === 'Lunch + vegetarian dinner' ? 2 : 1);
    const copy = JSON.parse(form.querySelector('[data-eq-prepaid-copy]').textContent);
    form.querySelector('[data-eq-plan-meal-count]').textContent = copy.mealCount.replace('__DAYS__', days).replace('__MEALS__', meals);
    if (fromOptions) {
      const url = new URL(location.href);
      url.searchParams.set('variant', selected.id);
      history.replaceState(history.state, '', url.toString());
    }
  };
  const refreshSection = async () => {
    const section = document.querySelector('[data-eq-cart-section]');
    if (!section || queue.pending || queue.error || renderedRevision === queue.savedRevision) return;
    const saved = queue.savedRevision;
    const revision = queue.revision;
    const serial = ++sectionSerial;
    try {
      const url = new URL(`${root}cart`, location.origin);
      url.searchParams.set('section_id', section.dataset.eqCartSection);
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-eq-cart-section]');
      if (!fresh || queue.pending || queue.error || revision !== queue.revision || serial !== sectionSerial) return;
      const active = document.activeElement;
      const activeRow = active?.closest('[data-eq-cart-item]');
      const focus = { identity: activeRow?.dataset.eqIdentity, delta: active?.dataset.eqDelta, note: active?.name === 'note', start: active?.selectionStart, end: active?.selectionEnd };
      fresh.querySelectorAll('noscript').forEach(node => node.remove());
      section.replaceWith(fresh);
      renderedRevision = saved;
      render();
      if (focus.note) {
        const input = fresh.querySelector('[name="note"]');
        input?.focus({ preventScroll: true }); input?.setSelectionRange(focus.start, focus.end);
      } else if (focus.identity) {
        const row = Array.from(fresh.querySelectorAll('[data-eq-cart-item]')).find(row => row.dataset.eqIdentity === focus.identity);
        const control = row && Array.from(row.querySelectorAll('[data-eq-delta]')).find(node => node.dataset.eqDelta === focus.delta);
        (control || fresh.querySelector('.eq-cart-update a') || fresh.querySelector('.eq-cart-empty a'))?.focus({ preventScroll: true });
      }
    } catch (_) { }
  };
  const render = () => {
    document.querySelectorAll('[data-eq-cart-count]').forEach(node => { node.textContent = String(queue.cart.item_count); });
    document.querySelectorAll('[data-eq-cart-total]').forEach(node => { node.textContent = money(queue.cart.total_price); });
    document.querySelectorAll('[data-eq-menu-order]').forEach(form => {
      const target = formTarget(form);
      const quantity = queue.desired(target);
      const pending = queue.intents.has(EquiBowlCartQueue.identity(target));
      form.querySelector('[data-eq-add]').hidden = quantity > 0;
      form.querySelector('[data-eq-menu-stepper]').hidden = quantity === 0;
      form.querySelector('[data-eq-quantity]').textContent = String(quantity);
      const state = form.querySelector('[data-eq-control-state]');
      state.textContent = pending ? config.pending_short : '';
      form.dataset.saving = String(pending);
      form.setAttribute('aria-busy', String(pending));
    });
    document.querySelectorAll('[data-eq-cart-item]').forEach(row => {
      const target = getRowTarget(row);
      if (!target) return;
      const quantity = queue.desired(target);
      const items = queue.items(target);
      const pending = queue.intents.has(EquiBowlCartQueue.identity(target));
      row.dataset.saving = String(pending);
      row.querySelector('[name="updates[]"]').value = String(quantity);
      row.querySelector('[name="updates[]"]').disabled = true;
      row.querySelector('[data-eq-quantity]').textContent = String(quantity);
      row.querySelector('[data-eq-delta="-1"]').disabled = quantity === 0;
      row.querySelector('[data-eq-line-total]').textContent = money(items.reduce((sum, item) => sum + item.final_line_price, 0));
      row.querySelector('[data-eq-control-state]').textContent = pending ? config.pending_short : '';
    });
    document.querySelectorAll('[data-eq-cart-subtotal]').forEach(node => { node.textContent = money(queue.cart.total_price); });
    document.querySelectorAll('[data-eq-plan-date-error]').forEach(node => { node.hidden = !invalidPlanDates(); });
    document.querySelectorAll('.eq-cart-checkout').forEach(button => { button.disabled = queue.pending || !!queue.error || invalidPlanDates(); });
    document.querySelectorAll('.eq-cart-whatsapp').forEach(link => { link.setAttribute('aria-disabled', String(queue.pending || !!queue.error)); });
    const cartState = document.querySelector('[data-eq-cart-state]');
    if (cartState) cartState.textContent = queue.error ? config.sync_error : queue.pending ? config.saving : config.saved;
    const feedback = document.querySelector('[data-eq-feedback]');
    if (feedback) {
      clearTimeout(feedbackTimer);
      feedback.hidden = !queue.error && !queue.pending && queue.savedRevision === 0;
      feedback.classList.toggle('is-error', !!queue.error);
      feedback.querySelector('[data-eq-feedback-text]').textContent = queue.error ? `${config.sync_error} ${queue.error.message === config.sync_error ? '' : queue.error.message}` : queue.pending ? config.saving : config.saved;
      feedback.querySelector('[data-eq-retry]').hidden = !queue.error;
      feedback.querySelector('[data-eq-discard]').hidden = !queue.error || queue.intents.size === 0;
      if (!queue.pending && !queue.error) feedbackTimer = setTimeout(() => { feedback.hidden = true; }, 2200);
    }
    const dock = document.querySelector('[data-eq-order-dock]');
    if (dock) { dock.hidden = queue.cart.item_count === 0; dock.querySelector('[data-eq-dock-status]').textContent = queue.pending ? config.saving : ''; }
    if (!queue.pending && !queue.error && queue.savedRevision !== renderedRevision) { clearTimeout(sectionTimer); sectionTimer = setTimeout(refreshSection, 120); }
  };
  const queue = new EquiBowlCartQueue(api, config.cart, render);
  const setup = () => {
    document.querySelectorAll('[data-eq-cart-item]').forEach(getRowTarget);
    document.querySelectorAll('[data-eq-variant-select]').forEach(updateProductVariant);
    document.querySelectorAll('[data-eq-product-stepper] input').forEach(input => { input.readOnly = true; });
    document.querySelectorAll('[data-eq-product-stepper] [data-eq-local-delta="-1"]').forEach(button => { button.disabled = Number(button.parentElement.querySelector('input').value) <= 1; });
    document.querySelectorAll('[data-eq-prepaid-form]').forEach(form => { setStartMinimum(form); setupPlanForm(form); });
    render();
  };
  document.addEventListener('click', async event => {
    const button = event.target.closest('button');
    if (button?.matches('[data-eq-retry]')) { await queue.retry(); return; }
    if (button?.matches('[data-eq-discard]')) { await queue.discard(); return; }
    if (button?.matches('[data-eq-local-delta]')) {
      const input = button.parentElement.querySelector('input');
      input.value = String(Math.max(1, Math.min(999, Number(input.value) + Number(button.dataset.eqLocalDelta))));
      button.parentElement.querySelector('[data-eq-local-delta="-1"]').disabled = Number(input.value) <= 1;
      return;
    }
    if (button?.matches('[data-eq-delta]')) {
      const form = button.closest('[data-eq-menu-order]');
      const row = button.closest('[data-eq-cart-item]');
      const target = form ? formTarget(form) : row ? getRowTarget(row) : null;
      if (target) queue.adjust(target, Number(button.dataset.eqDelta));
      return;
    }
    const link = event.target.closest('a');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.matches('[data-eq-remove]')) {
      event.preventDefault();
      const target = getRowTarget(link.closest('[data-eq-cart-item]'));
      if (target) queue.set(target, 0);
      return;
    }
    if (link.matches('.eq-cart-whatsapp') && (queue.pending || queue.error)) { event.preventDefault(); document.querySelector('[data-eq-retry]')?.focus(); return; }
    const url = new URL(link.href, location.href);
    if (url.origin === location.origin && (queue.pending || queue.error) && url.pathname !== location.pathname) {
      event.preventDefault();
      clearTimeout(noteTimer); await queue.run();
      if (!queue.error && !queue.pending) location.assign(link.href);
      else document.querySelector('[data-eq-retry]')?.focus();
    }
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('[data-eq-add-form]')) {
      event.preventDefault();
      setStartMinimum(form);
      if (!form.reportValidity()) return;
      const target = formTarget(form);
      queue.adjust(target, Math.max(1, Number(new FormData(form).get('quantity')) || 1));
      const status = form.querySelector('[data-eq-add-status]');
      if (status) {
        status.textContent = config.saving;
        queue.run().then(() => {
          status.textContent = queue.error ? config.sync_error : config.added + ' ';
          if (!queue.error) { const link = document.createElement('a'); link.href = `${root}cart`; link.textContent = config.view; status.append(link); }
        });
      }
    } else if (form.id === 'EquiBowlCart') {
      if (event.submitter?.name === 'checkout' && invalidPlanDates()) { event.preventDefault(); render(); document.querySelector('[data-eq-plan-date-error]')?.focus(); return; }
      if (queue.pending || queue.error) { event.preventDefault(); clearTimeout(noteTimer); queue.run(); return; }
      if (event.submitter?.name !== 'checkout') event.preventDefault();
    }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-eq-plan-option]')) setupPlanForm(event.target.closest('form'), true);
    if (event.target.matches('[data-eq-variant-select]')) { updateProductVariant(event.target); if (event.target.closest('[data-eq-prepaid-form]')) setupPlanForm(event.target.closest('form')); }
  });
  document.addEventListener('input', event => {
    if (event.target.matches('#EquiBowlCart [name="note"]')) {
      clearTimeout(noteTimer); queue.setNote(event.target.value, false); noteTimer = setTimeout(() => queue.run(), 450);
    }
  });
  window.addEventListener('pageshow', () => { setup(); queue.refresh(); });
  document.addEventListener('shopify:section:load', setup);
  setup();
  queue.refresh();
})();


